import React, { useState } from 'react';
import { Row, Typography, Tooltip, message, Checkbox } from 'antd';
import { EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import truncate from 'lodash/truncate';
import statesJSON from '../../../../../../utils/states.json'

const { Text } = Typography

import { 
    StyledCard,
    Title,
    StyledCardFooter,
    StyledButtonGroup,
    StyledCol,
    DeleteButton,
    EditButton,
    DuplicateButton,
    StyledTag,
    SubtypeBadge,
} from './styles'

const truncateString = str => truncate(str, { length: 35 })

const TemplateAttribute = ({ name, value, width, children }) => {
    return (
        <StyledCol span={width}>
            <div><strong>{name}</strong> <span>{value}</span></div>
            { children }
        </StyledCol>
    )
}

const TelemedicineCategoryCollection = ({ telemedicineCategories }) => {
    const categoryNames = telemedicineCategories.map(category => category.name)
    return (
        <StyledCol span={24} style={{ maxHeight: '200px', overflowY: 'scroll', padding: '8px 0'}}>
            { categoryNames.map(name => (
                <StyledTag>{name}</StyledTag>
            ))}
        </StyledCol>
    )
}

const CopyableNDC = ({ ndc }) => {
    const [copied, setCopied] = useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(ndc).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        }).catch(() => message.error('Failed to copy NDC'))
    }

    return (
        <Tooltip title={copied ? 'Copied!' : 'Click to copy'}>
            <StyledTag onClick={handleCopy} style={{ cursor: 'pointer' }}>{ndc}</StyledTag>
        </Tooltip>
    )
}

const NDCCollection = ({ pseudonyms }) => {
    const ndcs = pseudonyms.filter(pseudonym => pseudonym.length === 11)
    if (!ndcs.length) return "None"
    return (
        <StyledCol span={24} style={{ maxHeight: '200px', overflowY: 'scroll', padding: '8px 0'}}>
            { ndcs.map((ndc, index) => (
                <CopyableNDC key={`${ndc}-${index}`} ndc={ndc} />
            ))}
        </StyledCol>
    )
}
const getStateAbbreviation = state => Object.entries(statesJSON).find(([key, value]) => (
    value?.toLowerCase() === state?.name.toLowerCase())
)[0]?.toUpperCase()

export const Template = React.memo(({ template, selectTemplate, duplicateTemplate, handleDeleteTemplate, isSelected, onToggleSelect }) => {
    const {
        _id,
        medication,
        pharmacyNotes,
        directions,
        title,
        createdAt,
        updatedAt,
        pharmacyName,
        teleMedicineCategoryIds,
        DAW,
        states,
        subtype = 'medication',
    } = template

    const {
        drugName,
        strength,
        pillCount,
        refills,
        daysSupply,
        pseudonyms = [],
    } = medication ?? {}

    return (
        <StyledCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Title style={{ margin: 0 }}>{ title }</Title>
                <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelect?.(_id)}
                    style={{ marginLeft: '8px', flexShrink: 0 }}
                />
            </div>
            <Row gutter={[16, 16]}>
                <TemplateAttribute name="Medication" value={drugName} width={12} />
                <TemplateAttribute name="Strength" value={strength} width={12} />
                { !isNaN(parseInt(pillCount)) && <TemplateAttribute name="Pill Count" value={pillCount} width={7} /> }
                { !isNaN(parseInt(refills)) && <TemplateAttribute name="Refills" value={refills} width={7} /> }
                { !isNaN(parseInt(daysSupply)) && <TemplateAttribute name="Days Supply" value={daysSupply} width={10} /> }
                <TemplateAttribute name="Pharmacy Notes" value={!!pharmacyNotes?.length ? truncateString(pharmacyNotes) : 'none'} width={24} />
                <TemplateAttribute name="Patient Directions" value={truncateString(directions)} width={24} />
                <TemplateAttribute name="Categories" width={24}>
                    <TelemedicineCategoryCollection telemedicineCategories={teleMedicineCategoryIds}/>
                </TemplateAttribute>
                <TemplateAttribute name="NDCs" width={24}>
                    <NDCCollection pseudonyms={pseudonyms}/>
                </TemplateAttribute>
                { DAW && <TemplateAttribute name="" value={"*Dispense As Written"} width={24} /> }
                {
                    !!states?.length && (
                        <>
                            <TemplateAttribute name="States" value={states.map(getStateAbbreviation).join(', ')} width={24}/>
                        </>
                    )
                }{
                    !!pharmacyName && <TemplateAttribute name="Pharmacy" value={pharmacyName} width={24} />
                }
            </Row>
            <StyledCardFooter>
                <div style={{ textAlign: 'center' }}>
                    <Row justify="center"><Text style={{ fontSize: '.75rem' }}>Created at: { new Date(createdAt ?? updatedAt).toLocaleDateString() }</Text></Row>
                    <Row justify="center"><Text style={{ fontSize: '.75rem' }}>Last updated: { new Date(updatedAt).toLocaleDateString() }</Text></Row>
                    <SubtypeBadge subtype={subtype}>{subtype === 'supply' ? 'Supply' : 'Medication'}</SubtypeBadge>
                </div>
                <StyledButtonGroup>
                    <Tooltip title="Delete"><DeleteButton shape="circle" icon={<DeleteOutlined />} onClick={() => handleDeleteTemplate(_id)} /></Tooltip>
                    <Tooltip title="Duplicate"><DuplicateButton shape="circle" icon={<CopyOutlined />} onClick={() => duplicateTemplate(template)} /></Tooltip>
                    <Tooltip title="Edit"><EditButton shape="circle" icon={<EditOutlined />} onClick={() => selectTemplate(template)} /></Tooltip>
                </StyledButtonGroup>
            </StyledCardFooter>
        </StyledCard>
    );
});

